import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/context/AuthContext";
import {
    ArrowLeft, Send, Loader2, Sparkles, Trash2, Linkedin, Instagram, ExternalLink,
    CheckCircle2, XCircle, Activity, Copy, Check, MoreVertical, MessageSquare, Settings,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
    active: { label: "Active", icon: Activity, cls: "bg-signal/10 text-signal border-signal/30" },
    booked: { label: "Booked", icon: CheckCircle2, cls: "bg-green-500/10 text-green-400 border-green-500/30" },
    lost: { label: "Lost", icon: XCircle, cls: "bg-zinc-800 text-zinc-500 border-zinc-700" },
};
const METHOD_LABELS = { nepq: "NEPQ · Jeremy Miner", spin: "SPIN", challenger: "Challenger", sandler: "Sandler", meddic: "MEDDIC", gap: "Gap Selling", consultative: "Consultative", straight_line: "Straight Line" };

export default function ClientDetail() {
    const { id } = useParams();
    const { authHeaders } = useAuth();
    const navigate = useNavigate();
    const [thread, setThread] = useState(null);
    const [loading, setLoading] = useState(true);
    const [input, setInput] = useState("");
    const [role, setRole] = useState("them");
    const [autoReply, setAutoReply] = useState(true);
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(null);
    const [showSettings, setShowSettings] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/clients/${id}`, { headers: authHeaders() });
            setThread(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load thread");
        } finally { setLoading(false); }
    }, [authHeaders, id]);

    useEffect(() => { load(); }, [load]);

    const addTurn = async () => {
        if (!input.trim()) {
            toast.error("Type the message first.");
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(
                `${API}/clients/${id}/turn`,
                { role, text: input.trim(), generate_reply: role === "them" && autoReply },
                { headers: authHeaders() }
            );
            setThread(res.data);
            setInput("");
            toast.success(role === "them" && autoReply ? "Message logged · 3 replies ready" : "Message logged");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add message");
        } finally { setSending(false); }
    };

    const pickVariant = async (turnId, variant) => {
        try {
            const res = await axios.post(
                `${API}/clients/${id}/turn/${turnId}/pick`,
                { text: variant.text, variant_id: variant.id },
                { headers: authHeaders() }
            );
            setThread(res.data);
            toast.success("Saved as your reply");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const deleteTurn = async (turnId) => {
        if (!window.confirm("Delete this turn? This can't be undone.")) return;
        try {
            const res = await axios.delete(`${API}/clients/${id}/turn/${turnId}`, { headers: authHeaders() });
            setThread(res.data);
            toast.success("Deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const updateThread = async (patch) => {
        try {
            const res = await axios.patch(`${API}/clients/${id}`, patch, { headers: authHeaders() });
            setThread(res.data);
            toast.success("Updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const deleteThread = async () => {
        if (!window.confirm(`Delete the entire thread for ${thread.name}? This can't be undone.`)) return;
        try {
            await axios.delete(`${API}/clients/${id}`, { headers: authHeaders() });
            toast.success("Thread deleted");
            navigate("/app/clients");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const copy = async (text, key) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        } catch { toast.error("Copy failed"); }
    };

    if (loading) return <div className="text-zinc-500" data-testid="thread-loading">Loading thread…</div>;
    if (!thread) return null;

    const status = STATUS_META[thread.status] || STATUS_META.active;
    const StatusIcon = status.icon;
    const Platform = thread.platform === "instagram" ? Instagram : Linkedin;

    return (
        <div data-testid="thread-detail-page" className="max-w-4xl">
            {/* Header */}
            <div className="mb-6">
                <Link to="/app/clients" data-testid="back-to-clients" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white mb-3">
                    <ArrowLeft className="w-3.5 h-3.5" /> All clients
                </Link>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap mb-2">
                            <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight" data-testid="thread-name">{thread.name}</h1>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest border ${status.cls}`} data-testid="thread-status">
                                <StatusIcon className="w-3 h-3" /> {status.label}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-widest text-zinc-500">
                            {thread.prospect_company && <span>{thread.prospect_company}</span>}
                            {thread.prospect_company && <span>·</span>}
                            <span className="inline-flex items-center gap-1"><Platform className="w-3 h-3" /> {thread.platform}</span>
                            <span>·</span>
                            <span className="text-signal" data-testid="thread-methodology">{METHOD_LABELS[thread.methodology]}</span>
                            <span>·</span>
                            <span>{thread.tone}</span>
                            <span>·</span>
                            <span>{thread.industry}</span>
                            {thread.prospect_url && (
                                <>
                                    <span>·</span>
                                    <a href={thread.prospect_url.startsWith("http") ? thread.prospect_url : `https://${thread.prospect_url}`} target="_blank" rel="noreferrer" className="text-signal hover:underline inline-flex items-center gap-1">
                                        Profile <ExternalLink className="w-3 h-3" />
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => setShowSettings((s) => !s)}
                        data-testid="thread-settings-btn"
                        className="inline-flex items-center gap-2 border border-zinc-800 hover:border-zinc-600 text-zinc-300 hover:text-white px-3 py-2 rounded-md text-xs"
                    >
                        <Settings className="w-3.5 h-3.5" /> Settings
                    </button>
                </div>

                {showSettings && (
                    <div className="mt-4 border border-zinc-900 bg-[#0c0c0c] rounded-md p-5 grid sm:grid-cols-2 gap-4" data-testid="thread-settings-panel">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Status</label>
                            <select
                                value={thread.status}
                                onChange={(e) => updateThread({ status: e.target.value })}
                                data-testid="thread-status-select"
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-signal"
                            >
                                <option value="active">Active</option>
                                <option value="booked">Booked</option>
                                <option value="lost">Lost</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Methodology</label>
                            <select
                                value={thread.methodology}
                                onChange={(e) => updateThread({ methodology: e.target.value })}
                                data-testid="thread-methodology-select"
                                className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-signal"
                            >
                                {Object.entries(METHOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <button
                                onClick={deleteThread}
                                data-testid="delete-thread-btn"
                                className="text-xs text-zinc-500 hover:text-red-400 inline-flex items-center gap-1.5 mt-2"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete entire thread
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Turns */}
            <div className="space-y-4 mb-6" data-testid="turns-list">
                {(thread.turns || []).length === 0 && (
                    <div className="border border-dashed border-zinc-900 p-12 text-center text-zinc-500">
                        <MessageSquare className="w-8 h-8 mx-auto mb-3 text-zinc-700" strokeWidth={1.2} />
                        No messages yet. Paste their first DM below to get started.
                    </div>
                )}
                {(thread.turns || []).map((t) => {
                    const isMe = t.role === "me";
                    const hasVariants = t.ai_generated && t.ai_variants && !t.chosen_variant_id;
                    return (
                        <div key={t.id} data-testid={`turn-${t.id}`} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[88%] ${isMe ? "items-end" : "items-start"} flex flex-col gap-2`}>
                                <div className={`rounded-lg px-4 py-3 ${isMe ? "bg-signal/15 border border-signal/30" : "bg-zinc-900 border border-zinc-800"} group relative`}>
                                    <div className="flex items-center gap-2 mb-1 text-[10px] font-mono uppercase tracking-widest">
                                        <span className={isMe ? "text-signal" : "text-zinc-500"}>{isMe ? "Me" : "Them"}</span>
                                        {t.ai_generated && <span className="text-zinc-600">· AI · {METHOD_LABELS[thread.methodology]}</span>}
                                        <span className="text-zinc-700">· {new Date(t.created_at).toLocaleString()}</span>
                                        <button onClick={() => deleteTurn(t.id)} data-testid={`delete-turn-${t.id}`} className="ml-auto opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400 transition-opacity">
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                    {t.text ? (
                                        <div className="text-sm text-white whitespace-pre-wrap">{t.text}</div>
                                    ) : hasVariants ? (
                                        <div className="text-xs text-zinc-500 italic">Pick one of the AI drafts below ↓</div>
                                    ) : (
                                        <div className="text-xs text-zinc-600 italic">(empty)</div>
                                    )}
                                    {t.text && isMe && (
                                        <button onClick={() => copy(t.text, t.id)} data-testid={`copy-turn-${t.id}`} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-white transition-opacity">
                                            {copied === t.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                                        </button>
                                    )}
                                </div>
                                {hasVariants && (
                                    <div className="space-y-2 w-full">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-signal flex items-center gap-1">
                                            <Sparkles className="w-3 h-3" /> AI drafts — pick one to save as your reply
                                        </div>
                                        {t.ai_variants.map((v, i) => (
                                            <div key={v.id} data-testid={`variant-${v.id}`} className="border border-zinc-800 bg-[#0c0c0c] rounded-md p-3 hover:border-signal/40 transition-colors">
                                                <div className="flex items-center gap-2 mb-1.5 text-[10px] font-mono uppercase tracking-widest">
                                                    <span className="text-signal">Option {i + 1}</span>
                                                    <span className="text-zinc-600">· {v.angle}</span>
                                                    <span className="text-zinc-600">· {v.confidence}% conf.</span>
                                                </div>
                                                <div className="text-sm text-white whitespace-pre-wrap mb-3">{v.text}</div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => pickVariant(t.id, v)}
                                                        data-testid={`pick-variant-${v.id}`}
                                                        className="bg-signal hover:bg-signal-hover text-white px-3 py-1.5 rounded text-xs font-semibold transition-colors"
                                                    >
                                                        Use this reply
                                                    </button>
                                                    <button onClick={() => copy(v.text, v.id)} className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white px-3 py-1.5 rounded text-xs">
                                                        {copied === v.id ? <><Check className="w-3 h-3 inline" /> Copied</> : <><Copy className="w-3 h-3 inline" /> Copy</>}
                                                    </button>
                                                </div>
                                                {v.reasoning && <div className="text-[10px] text-zinc-600 mt-2 italic">Why: {v.reasoning}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Composer */}
            <div className="border border-zinc-900 bg-[#0c0c0c] rounded-md p-4 sticky bottom-4" data-testid="thread-composer">
                <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center gap-1 border border-zinc-800 rounded-md bg-zinc-950 p-0.5">
                        <button
                            onClick={() => setRole("them")}
                            data-testid="role-them-btn"
                            className={`px-3 py-1 rounded text-xs font-mono uppercase tracking-widest transition-colors ${role === "them" ? "bg-signal text-white" : "text-zinc-400 hover:text-white"}`}
                        >
                            They said
                        </button>
                        <button
                            onClick={() => setRole("me")}
                            data-testid="role-me-btn"
                            className={`px-3 py-1 rounded text-xs font-mono uppercase tracking-widest transition-colors ${role === "me" ? "bg-signal text-white" : "text-zinc-400 hover:text-white"}`}
                        >
                            I said
                        </button>
                    </div>
                    {role === "them" && (
                        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={autoReply}
                                onChange={(e) => setAutoReply(e.target.checked)}
                                data-testid="auto-reply-toggle"
                                className="accent-signal"
                            />
                            <Sparkles className="w-3 h-3 text-signal" /> Draft my reply
                        </label>
                    )}
                </div>
                <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    rows={3}
                    placeholder={role === "them" ? "Paste what the prospect just sent…" : "Type your own reply (already-sent message)…"}
                    data-testid="composer-input"
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-md text-white px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-signal focus:border-signal resize-none"
                />
                <div className="flex items-center justify-between mt-3">
                    <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
                        Methodology: <span className="text-signal">{METHOD_LABELS[thread.methodology]}</span>
                    </div>
                    <button
                        onClick={addTurn}
                        disabled={sending || !input.trim()}
                        data-testid="composer-submit"
                        className="bg-signal hover:bg-signal-hover text-white px-4 py-2 rounded-md font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-60"
                    >
                        {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</> : <>
                            <Send className="w-4 h-4" />
                            {role === "them" && autoReply ? "Log + Draft reply" : "Log message"}
                        </>}
                    </button>
                </div>
            </div>
        </div>
    );
}
